import { useProposals } from "@/hooks/useProposals";
import { Button } from "@/components/ui/button";
import { ProposalCard } from "./ProposalCard";
import { useState } from "react";
import { ProposalDialog } from "./ProposalDialog";
import { InsertProposal } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Loader, Plus } from "lucide-react";

interface ProposalListProps {
  tripId: number;
}

export function ProposalList({ tripId }: ProposalListProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const {
    proposals,
    isLoading,
    error,
    createProposal,
    isCreating,
    updateProposal,
    isUpdating,
    deleteProposal,
    isDeleting,
    generateProposal,
    isGenerating,
  } = useProposals(tripId);

  // Handle create/edit form submission
  const handleSubmit = (data: InsertProposal) => {
    if (selectedProposalId) {
      // Update existing proposal
      updateProposal(
        { id: selectedProposalId, data },
        {
          onSuccess: () => {
            toast({
              title: "Proposal updated",
              description: "The proposal has been updated successfully.",
            });
            setIsDialogOpen(false);
          },
          onError: (error) => {
            console.error("Error updating proposal:", error);
            toast({
              title: "Error",
              description: "Failed to update the proposal. Please try again.",
              variant: "destructive",
            });
          },
        }
      );
    } else {
      // Create new proposal
      createProposal(
        data,
        {
          onSuccess: () => {
            toast({
              title: "Proposal created",
              description: "The proposal has been created successfully.",
            });
            setIsDialogOpen(false);
          },
          onError: (error) => {
            console.error("Error creating proposal:", error);
            toast({
              title: "Error",
              description: "Failed to create the proposal. Please try again.",
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  // Handle opening the dialog for creating a new proposal
  const handleCreateProposal = () => {
    setSelectedProposalId(null);
    setIsDialogOpen(true);
  };
  
  // Handle AI-powered proposal generation
  const handleGenerateProposal = () => {
    generateProposal(undefined, {
      onSuccess: (proposal) => {
        toast({
          title: "Proposal generated",
          description: "AI has created a proposal based on your trip details.",
        });
      },
      onError: (error) => {
        console.error("Error generating proposal with AI:", error);
        toast({
          title: "Error",
          description: "Failed to generate proposal. Please try again.",
          variant: "destructive",
        });
      },
    });
  };

  // Handle opening the dialog for editing an existing proposal
  const handleEditProposal = (id: number) => {
    setSelectedProposalId(id);
    setIsDialogOpen(true);
  };

  // Handle viewing a proposal
  const handleViewProposal = (id: number) => {
    navigate(`/proposals/${id}`);
  };

  // Handle deleting a proposal
  const handleDeleteProposal = (id: number) => {
    deleteProposal(
      id,
      {
        onError: (error) => {
          console.error("Error deleting proposal:", error);
          toast({
            title: "Error",
            description: "Failed to delete the proposal. Please try again.",
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Trip Proposals</h2>
        <div className="flex gap-2">
          <Button 
            onClick={handleGenerateProposal} 
            variant="outline"
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" />
                  <path d="M16 3v4" />
                  <path d="M8 3v4" />
                  <path d="M16 21.5V17a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v4.5" />
                  <path d="M22 19a3 3 0 1 0-4 2.83V22l2-1 2 1v-.17A3 3 0 0 0 22 19z" />
                </svg>
                AI Generate
              </>
            )}
          </Button>
          <Button onClick={handleCreateProposal}>
            <Plus className="mr-2 h-4 w-4" />
            Create Proposal
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader className="animate-spin h-8 w-8 text-primary" />
          <span className="ml-2">Loading proposals...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          <p>Error loading proposals. Please try again.</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && proposals.length === 0 && (
        <div className="text-center py-8 border border-dashed rounded-lg">
          <h3 className="font-medium text-muted-foreground">No proposals yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first proposal to share with your client.
          </p>
          <Button onClick={handleCreateProposal} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Create Proposal
          </Button>
        </div>
      )}

      {/* Proposal list */}
      {!isLoading && !error && proposals.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {proposals.map((proposal) => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              onEdit={handleEditProposal}
              onView={handleViewProposal}
              onDelete={handleDeleteProposal}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <ProposalDialog
        tripId={tripId}
        proposalId={selectedProposalId}
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isUpdating}
      />
    </div>
  );
}